// CANONICAL COPY (GH #7115). src/constants/controlMappings.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * src/constants/controlMappings.js
 *
 * Cross-framework control mapping table.
 *
 * Each entry groups controls from different frameworks that address the same
 * compliance objective.  Standard tasks reference these via `mappingId`.
 *
 * After the TCF migration the canonical IDs are TCF-based, but this table
 * still stores the original external IDs per framework so the standard-task
 * and auto-evidence systems can resolve which TCF controls to target.
 *
 * Also re-exports TCF-aware helpers that use the control index.
 */
const { buildControlIndex } = require('./controlIndex.cjs');

// ── TCF control index (used by helpers at the bottom) ───────────────────
const { byExternalId, externalRefs, allFlat: _allFlat } = buildControlIndex();

// ═══════════════════════════════════════════════════════════════════════════
// Legacy mapping table — external IDs per framework
// ═══════════════════════════════════════════════════════════════════════════

const CONTROL_MAPPINGS = [
  // ── A.5 — Organizational controls ────────────────────────────────────────
  {
    id: 'map-policies',
    topic: 'Information security policies',
    iso27002: ['A.5.1'],
    iso27001: ['5.2', '7.5.1', '7.5.2'],
    cis18: ['CIS-15.2'],
  },
  {
    id: 'map-roles-responsibilities',
    topic: 'Information security roles and responsibilities',
    iso27002: ['A.5.2'],
    iso27001: ['5.3'],
    cis18: ['CIS-17.5', 'CIS-6.8'],
  },
  {
    id: 'map-segregation-duties',
    topic: 'Segregation of duties',
    iso27002: ['A.5.3'],
    iso27001: [],
    cis18: ['CIS-5.4', 'CIS-6.8'],
  },
  {
    id: 'map-management-responsibilities',
    topic: 'Management responsibilities',
    iso27002: ['A.5.4'],
    iso27001: ['5.1'],
    cis18: ['CIS-14.1'],
  },
  {
    id: 'map-contact-authorities',
    topic: 'Contact with authorities',
    iso27002: ['A.5.5'],
    iso27001: [],
    cis18: ['CIS-17.2'],
  },
  {
    id: 'map-contact-groups',
    topic: 'Contact with special interest groups',
    iso27002: ['A.5.6'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-threat-intelligence',
    topic: 'Threat intelligence',
    iso27002: ['A.5.7'],
    iso27001: [],
    cis18: ['CIS-7.5', 'CIS-7.6', 'CIS-13.1'],
  },
  {
    id: 'map-project-security',
    topic: 'Information security in project management',
    iso27002: ['A.5.8'],
    iso27001: [],
    cis18: ['CIS-16.1'],
  },
  {
    id: 'map-asset-inventory',
    topic: 'Inventory of information and other associated assets',
    iso27002: ['A.5.9'],
    iso27001: [],
    cis18: ['CIS-1.1', 'CIS-1.2', 'CIS-2.1'],
  },
  {
    id: 'map-acceptable-use',
    topic: 'Acceptable use of information and other associated assets',
    iso27002: ['A.5.10'],
    iso27001: [],
    cis18: ['CIS-14.4', 'CIS-14.5'],
  },
  {
    id: 'map-return-assets',
    topic: 'Return of assets',
    iso27002: ['A.5.11'],
    iso27001: [],
    cis18: ['CIS-1.2', 'CIS-6.2'],
  },
  {
    id: 'map-classification',
    topic: 'Classification of information',
    iso27002: ['A.5.12'],
    iso27001: [],
    cis18: ['CIS-3.7'],
  },
  {
    id: 'map-labelling',
    topic: 'Labelling of information',
    iso27002: ['A.5.13'],
    iso27001: [],
    cis18: ['CIS-3.7'],
  },
  {
    id: 'map-info-transfer',
    topic: 'Information transfer',
    iso27002: ['A.5.14'],
    iso27001: [],
    cis18: ['CIS-3.10'],
  },
  {
    id: 'map-access-control',
    topic: 'Access control',
    iso27002: ['A.5.15'],
    iso27001: [],
    cis18: ['CIS-3.3', 'CIS-6.7', 'CIS-6.8'],
  },
  {
    id: 'map-identity-mgmt',
    topic: 'Identity management',
    iso27002: ['A.5.16'],
    iso27001: [],
    cis18: ['CIS-5.1', 'CIS-5.5', 'CIS-5.6'],
  },
  {
    id: 'map-auth-info',
    topic: 'Authentication information',
    iso27002: ['A.5.17'],
    iso27001: [],
    cis18: ['CIS-5.2', 'CIS-6.3', 'CIS-6.4', 'CIS-6.5'],
  },
  {
    id: 'map-access-rights',
    topic: 'Access rights',
    iso27002: ['A.5.18'],
    iso27001: [],
    cis18: ['CIS-5.3', 'CIS-6.1', 'CIS-6.2'],
  },
  {
    id: 'map-supplier-relationships',
    topic: 'Information security in supplier relationships',
    iso27002: ['A.5.19'],
    iso27001: [],
    cis18: ['CIS-15.1', 'CIS-15.2', 'CIS-15.3'],
  },
  {
    id: 'map-supplier-agreements',
    topic: 'Addressing information security within supplier agreements',
    iso27002: ['A.5.20'],
    iso27001: [],
    cis18: ['CIS-15.4'],
  },
  {
    id: 'map-ict-supply-chain',
    topic: 'Managing information security in the ICT supply chain',
    iso27002: ['A.5.21'],
    iso27001: [],
    cis18: ['CIS-15.5', 'CIS-16.4', 'CIS-16.5'],
  },
  {
    id: 'map-supplier-monitoring',
    topic: 'Monitoring, review and change management of supplier services',
    iso27002: ['A.5.22'],
    iso27001: [],
    cis18: ['CIS-15.5', 'CIS-15.6'],
  },
  {
    id: 'map-cloud-security',
    topic: 'Information security for use of cloud services',
    iso27002: ['A.5.23'],
    iso27001: [],
    cis18: ['CIS-15.5', 'CIS-15.6'],
  },
  {
    id: 'map-incident-planning',
    topic: 'Information security incident management planning and preparation',
    iso27002: ['A.5.24'],
    iso27001: [],
    cis18: ['CIS-17.1', 'CIS-17.3', 'CIS-17.4'],
  },
  {
    id: 'map-incident-assessment',
    topic: 'Assessment and decision on information security events',
    iso27002: ['A.5.25'],
    iso27001: [],
    cis18: ['CIS-17.9'],
  },
  {
    id: 'map-incident-response',
    topic: 'Response to information security incidents',
    iso27002: ['A.5.26'],
    iso27001: [],
    cis18: ['CIS-17.4', 'CIS-17.6'],
  },
  {
    id: 'map-incident-learning',
    topic: 'Learning from information security incidents',
    iso27002: ['A.5.27'],
    iso27001: [],
    cis18: ['CIS-17.8'],
  },
  {
    id: 'map-evidence-collection',
    topic: 'Collection of evidence',
    iso27002: ['A.5.28'],
    iso27001: [],
    cis18: ['CIS-8.1', 'CIS-8.2', 'CIS-8.5'],
  },
  {
    id: 'map-disruption-security',
    topic: 'Information security during disruption',
    iso27002: ['A.5.29'],
    iso27001: [],
    cis18: ['CIS-11.1'],
  },
  {
    id: 'map-ict-continuity',
    topic: 'ICT readiness for business continuity',
    iso27002: ['A.5.30'],
    iso27001: [],
    cis18: ['CIS-11.1', 'CIS-11.2', 'CIS-11.4', 'CIS-11.5'],
  },
  {
    id: 'map-legal-requirements',
    topic: 'Legal, statutory, regulatory and contractual requirements',
    iso27002: ['A.5.31'],
    iso27001: ['4.2'],
    cis18: [],
  },
  {
    id: 'map-ip-rights',
    topic: 'Intellectual property rights',
    iso27002: ['A.5.32'],
    iso27001: [],
    cis18: ['CIS-2.1', 'CIS-2.2'],
  },
  {
    id: 'map-records-protection',
    topic: 'Protection of records',
    iso27002: ['A.5.33'],
    iso27001: ['7.5.3'],
    cis18: ['CIS-3.4', 'CIS-8.10'],
  },
  {
    id: 'map-privacy-pii',
    topic: 'Privacy and protection of PII',
    iso27002: ['A.5.34'],
    iso27001: [],
    cis18: ['CIS-3.1', 'CIS-3.14'],
  },
  {
    id: 'map-independent-review',
    topic: 'Independent review of information security',
    iso27002: ['A.5.35'],
    iso27001: ['9.2.1', '9.2.2'],
    cis18: ['CIS-18.1', 'CIS-18.2', 'CIS-18.5'],
  },
  {
    id: 'map-compliance-review',
    topic: 'Compliance with policies, rules and standards',
    iso27002: ['A.5.36'],
    iso27001: ['9.1'],
    cis18: ['CIS-4.1'],
  },
  {
    id: 'map-operating-procedures',
    topic: 'Documented operating procedures',
    iso27002: ['A.5.37'],
    iso27001: ['7.5.1'],
    cis18: [],
  },

  // ── A.6 — People controls ───────────────────────────────────────────────
  { id: 'map-screening', topic: 'Screening', iso27002: ['A.6.1'], iso27001: [], cis18: [] },
  {
    id: 'map-employment-terms',
    topic: 'Terms and conditions of employment',
    iso27002: ['A.6.2'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-awareness-training',
    topic: 'Information security awareness, education and training',
    iso27002: ['A.6.3'],
    iso27001: ['7.2', '7.3'],
    cis18: [
      'CIS-14.1',
      'CIS-14.2',
      'CIS-14.3',
      'CIS-14.4',
      'CIS-14.5',
      'CIS-14.6',
      'CIS-14.7',
      'CIS-14.8',
      'CIS-14.9',
    ],
  },
  {
    id: 'map-disciplinary',
    topic: 'Disciplinary process',
    iso27002: ['A.6.4'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-post-employment',
    topic: 'Responsibilities after termination or change of employment',
    iso27002: ['A.6.5'],
    iso27001: [],
    cis18: ['CIS-6.2'],
  },
  {
    id: 'map-nda',
    topic: 'Confidentiality or non-disclosure agreements',
    iso27002: ['A.6.6'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-remote-working',
    topic: 'Remote working',
    iso27002: ['A.6.7'],
    iso27001: [],
    cis18: ['CIS-12.7', 'CIS-14.8'],
  },
  {
    id: 'map-event-reporting',
    topic: 'Information security event reporting',
    iso27002: ['A.6.8'],
    iso27001: [],
    cis18: ['CIS-17.3', 'CIS-14.6'],
  },

  // ── A.7 — Physical controls ─────────────────────────────────────────────
  {
    id: 'map-physical-perimeters',
    topic: 'Physical security perimeters',
    iso27002: ['A.7.1'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-physical-entry',
    topic: 'Physical entry',
    iso27002: ['A.7.2'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-securing-facilities',
    topic: 'Securing offices, rooms and facilities',
    iso27002: ['A.7.3'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-physical-monitoring',
    topic: 'Physical security monitoring',
    iso27002: ['A.7.4'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-environmental-threats',
    topic: 'Protecting against physical and environmental threats',
    iso27002: ['A.7.5'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-secure-areas',
    topic: 'Working in secure areas',
    iso27002: ['A.7.6'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-clear-desk',
    topic: 'Clear desk and clear screen',
    iso27002: ['A.7.7'],
    iso27001: [],
    cis18: ['CIS-4.3'],
  },
  {
    id: 'map-equipment-siting',
    topic: 'Equipment siting and protection',
    iso27002: ['A.7.8'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-offsite-assets',
    topic: 'Security of assets off-premises',
    iso27002: ['A.7.9'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-storage-media',
    topic: 'Storage media',
    iso27002: ['A.7.10'],
    iso27001: [],
    cis18: ['CIS-3.5', 'CIS-3.9'],
  },
  {
    id: 'map-utilities',
    topic: 'Supporting utilities',
    iso27002: ['A.7.11'],
    iso27001: [],
    cis18: [],
  },
  { id: 'map-cabling', topic: 'Cabling security', iso27002: ['A.7.12'], iso27001: [], cis18: [] },
  {
    id: 'map-equipment-maintenance',
    topic: 'Equipment maintenance',
    iso27002: ['A.7.13'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-equipment-disposal',
    topic: 'Secure disposal or re-use of equipment',
    iso27002: ['A.7.14'],
    iso27001: [],
    cis18: ['CIS-3.5'],
  },

  // ── A.8 — Technological controls ────────────────────────────────────────
  {
    id: 'map-endpoint-devices',
    topic: 'User endpoint devices',
    iso27002: ['A.8.1'],
    iso27001: [],
    cis18: ['CIS-1.1', 'CIS-4.1', 'CIS-4.3'],
  },
  {
    id: 'map-privileged-access',
    topic: 'Privileged access rights',
    iso27002: ['A.8.2'],
    iso27001: [],
    cis18: ['CIS-5.4', 'CIS-6.5'],
  },
  {
    id: 'map-info-access-restriction',
    topic: 'Information access restriction',
    iso27002: ['A.8.3'],
    iso27001: [],
    cis18: ['CIS-3.3', 'CIS-6.8'],
  },
  {
    id: 'map-source-code-access',
    topic: 'Access to source code',
    iso27002: ['A.8.4'],
    iso27001: [],
    cis18: ['CIS-16.1'],
  },
  {
    id: 'map-secure-auth',
    topic: 'Secure authentication',
    iso27002: ['A.8.5'],
    iso27001: [],
    cis18: ['CIS-6.3', 'CIS-6.4', 'CIS-6.5'],
  },
  {
    id: 'map-capacity',
    topic: 'Capacity management',
    iso27002: ['A.8.6'],
    iso27001: [],
    cis18: ['CIS-8.3'],
  },
  {
    id: 'map-malware',
    topic: 'Protection against malware',
    iso27002: ['A.8.7'],
    iso27001: [],
    cis18: ['CIS-10.1', 'CIS-10.2', 'CIS-10.3', 'CIS-10.4', 'CIS-10.5', 'CIS-10.6', 'CIS-10.7'],
  },
  {
    id: 'map-vulnerability-mgmt',
    topic: 'Management of technical vulnerabilities',
    iso27002: ['A.8.8'],
    iso27001: [],
    cis18: ['CIS-7.1', 'CIS-7.2', 'CIS-7.3', 'CIS-7.4', 'CIS-7.5', 'CIS-7.6', 'CIS-7.7'],
  },
  {
    id: 'map-config-mgmt',
    topic: 'Configuration management',
    iso27002: ['A.8.9'],
    iso27001: [],
    cis18: ['CIS-4.1', 'CIS-4.2', 'CIS-4.6', 'CIS-4.7', 'CIS-4.8'],
  },
  {
    id: 'map-info-deletion',
    topic: 'Information deletion',
    iso27002: ['A.8.10'],
    iso27001: [],
    cis18: ['CIS-3.4', 'CIS-3.5'],
  },
  {
    id: 'map-data-masking',
    topic: 'Data masking',
    iso27002: ['A.8.11'],
    iso27001: [],
    cis18: ['CIS-3.12'],
  },
  {
    id: 'map-dlp',
    topic: 'Data leakage prevention',
    iso27002: ['A.8.12'],
    iso27001: [],
    cis18: ['CIS-3.13'],
  },
  {
    id: 'map-backup',
    topic: 'Information backup',
    iso27002: ['A.8.13'],
    iso27001: [],
    cis18: ['CIS-11.2', 'CIS-11.3', 'CIS-11.4', 'CIS-11.5'],
  },
  {
    id: 'map-redundancy',
    topic: 'Redundancy of information processing facilities',
    iso27002: ['A.8.14'],
    iso27001: [],
    cis18: [],
  },
  {
    id: 'map-logging',
    topic: 'Logging',
    iso27002: ['A.8.15'],
    iso27001: [],
    cis18: ['CIS-8.1', 'CIS-8.2', 'CIS-8.5', 'CIS-8.9', 'CIS-8.10', 'CIS-8.12'],
  },
  {
    id: 'map-monitoring',
    topic: 'Monitoring activities',
    iso27002: ['A.8.16'],
    iso27001: [],
    cis18: ['CIS-8.11', 'CIS-13.1', 'CIS-13.2', 'CIS-13.3', 'CIS-13.6'],
  },
  {
    id: 'map-clock-sync',
    topic: 'Clock synchronization',
    iso27002: ['A.8.17'],
    iso27001: [],
    cis18: ['CIS-8.4'],
  },
  {
    id: 'map-privileged-utils',
    topic: 'Use of privileged utility programs',
    iso27002: ['A.8.18'],
    iso27001: [],
    cis18: ['CIS-4.8'],
  },
  {
    id: 'map-software-install',
    topic: 'Installation of software on operational systems',
    iso27002: ['A.8.19'],
    iso27001: [],
    cis18: ['CIS-2.3', 'CIS-2.5'],
  },
  {
    id: 'map-network-security',
    topic: 'Networks security',
    iso27002: ['A.8.20'],
    iso27001: [],
    cis18: ['CIS-12.1', 'CIS-12.2', 'CIS-12.3'],
  },
  {
    id: 'map-network-services',
    topic: 'Security of network services',
    iso27002: ['A.8.21'],
    iso27001: [],
    cis18: ['CIS-12.6'],
  },
  {
    id: 'map-network-segregation',
    topic: 'Segregation of networks',
    iso27002: ['A.8.22'],
    iso27001: [],
    cis18: ['CIS-12.2', 'CIS-13.4'],
  },
  {
    id: 'map-web-filtering',
    topic: 'Web filtering',
    iso27002: ['A.8.23'],
    iso27001: [],
    cis18: ['CIS-9.2', 'CIS-9.3'],
  },
  {
    id: 'map-cryptography',
    topic: 'Use of cryptography',
    iso27002: ['A.8.24'],
    iso27001: [],
    cis18: ['CIS-3.6', 'CIS-3.10', 'CIS-3.11'],
  },
  {
    id: 'map-sdlc',
    topic: 'Secure development life cycle',
    iso27002: ['A.8.25'],
    iso27001: [],
    cis18: ['CIS-16.1', 'CIS-16.9', 'CIS-16.10'],
  },
  {
    id: 'map-app-security-reqs',
    topic: 'Application security requirements',
    iso27002: ['A.8.26'],
    iso27001: [],
    cis18: ['CIS-16.10'],
  },
  {
    id: 'map-secure-architecture',
    topic: 'Secure system architecture and engineering principles',
    iso27002: ['A.8.27'],
    iso27001: [],
    cis18: ['CIS-16.7', 'CIS-16.10'],
  },
  {
    id: 'map-secure-coding',
    topic: 'Secure coding',
    iso27002: ['A.8.28'],
    iso27001: [],
    cis18: ['CIS-16.1', 'CIS-16.12'],
  },
  {
    id: 'map-security-testing',
    topic: 'Security testing in development and acceptance',
    iso27002: ['A.8.29'],
    iso27001: [],
    cis18: ['CIS-16.12', 'CIS-16.13'],
  },
  {
    id: 'map-outsourced-dev',
    topic: 'Outsourced development',
    iso27002: ['A.8.30'],
    iso27001: [],
    cis18: ['CIS-16.4', 'CIS-16.5'],
  },
  {
    id: 'map-env-separation',
    topic: 'Separation of development, test and production environments',
    iso27002: ['A.8.31'],
    iso27001: [],
    cis18: ['CIS-16.8'],
  },
  {
    id: 'map-change-mgmt',
    topic: 'Change management',
    iso27002: ['A.8.32'],
    iso27001: ['6.3'],
    cis18: ['CIS-4.1'],
  },
  { id: 'map-test-info', topic: 'Test information', iso27002: ['A.8.33'], iso27001: [], cis18: [] },
  {
    id: 'map-audit-testing',
    topic: 'Protection of information systems during audit testing',
    iso27002: ['A.8.34'],
    iso27001: [],
    cis18: ['CIS-18.4'],
  },

  // ── ISO 27001 ISMS clauses (no direct Annex A counterpart) ──────────────
  {
    id: 'map-isms-context',
    topic: 'ISMS context and scope',
    iso27002: [],
    iso27001: ['4.1', '4.2', '4.3', '4.4'],
    cis18: [],
  },
  {
    id: 'map-isms-risk-assessment',
    topic: 'Information security risk assessment',
    iso27002: [],
    iso27001: ['6.1.1', '6.1.2', '6.1.3', '8.2', '8.3'],
    cis18: ['CIS-7.1'],
  },
  {
    id: 'map-isms-objectives',
    topic: 'Information security objectives',
    iso27002: [],
    iso27001: ['6.2'],
    cis18: [],
  },
  { id: 'map-isms-resources', topic: 'Resources', iso27002: [], iso27001: ['7.1'], cis18: [] },
  {
    id: 'map-isms-communication',
    topic: 'Communication',
    iso27002: [],
    iso27001: ['7.4'],
    cis18: [],
  },
  {
    id: 'map-isms-operational-planning',
    topic: 'Operational planning and control',
    iso27002: [],
    iso27001: ['8.1'],
    cis18: [],
  },
  {
    id: 'map-isms-performance',
    topic: 'Monitoring, measurement, analysis and evaluation',
    iso27002: [],
    iso27001: ['9.1'],
    cis18: [],
  },
  {
    id: 'map-isms-mgmt-review',
    topic: 'Management review',
    iso27002: [],
    iso27001: ['9.3.1', '9.3.2', '9.3.3'],
    cis18: [],
  },
  {
    id: 'map-isms-improvement',
    topic: 'Continual improvement and corrective action',
    iso27002: [],
    iso27001: ['10.1', '10.2'],
    cis18: [],
  },
];

// ── Lookup helpers ──────────────────────────────────────────────────────────

/** Map<mappingId, entry> for O(1) lookup */
const MAPPINGS_BY_ID = new Map(CONTROL_MAPPINGS.map((m) => [m.id, m]));

/** Reverse lookup: controlId → mapping group ids */
function buildReverseLookup() {
  const map = new Map();
  for (const entry of CONTROL_MAPPINGS) {
    const allIds = [...(entry.iso27002 || []), ...(entry.iso27001 || []), ...(entry.cis18 || [])];
    for (const id of allIds) {
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(entry.id);
    }
  }
  return map;
}
const CONTROL_TO_MAPPINGS = buildReverseLookup();

// ── TCF-aware helpers ───────────────────────────────────────────────────────

/**
 * Given any control ID (TCF or legacy external), return all related
 * control IDs across all frameworks that map to the same TCF control.
 */
function getEquivalentControls(controlId) {
  if (typeof controlId !== 'string') return [controlId];
  const scfId = controlId.startsWith('SCF-') ? controlId : byExternalId.get(controlId);
  if (!scfId) return [controlId];
  const refs = externalRefs.get(scfId) || [];
  return [scfId, ...refs.map((r) => r.externalId)];
}

/**
 * Given a set of control IDs the tenant already covers and a list of
 * target framework control IDs, returns covered and gap arrays.
 */
function analyzeFrameworkCoverage(existingControlIds, targetFrameworkControlIds) {
  const existing = new Set(
    existingControlIds.map((id) =>
      typeof id === 'string' && id.startsWith('SCF-') ? id : (byExternalId.get(id) ?? id)
    )
  );
  const covered = [];
  const gaps = [];

  for (const targetId of targetFrameworkControlIds) {
    const scfId =
      typeof targetId === 'string' && targetId.startsWith('SCF-')
        ? targetId
        : byExternalId.get(targetId);
    if (scfId && existing.has(scfId)) {
      covered.push({ controlId: targetId, coveredBy: [scfId] });
    } else {
      gaps.push({ controlId: targetId });
    }
  }

  return { covered, gaps };
}

// CONTROL_MAPPINGS is intentionally NOT exported — it is a legacy table kept
// only to populate MAPPINGS_BY_ID for backward-compat with old persisted state
// that still carries standardTaskMappingId: 'map-xxx'.  All new code uses
// scfId on task templates and calls getEquivalentControls() directly.

module.exports = {
  CONTROL_TO_MAPPINGS,
  MAPPINGS_BY_ID,
  getEquivalentControls,
  analyzeFrameworkCoverage,
};
